/**
 * @module composables/useTasks
 * @description Singleton state + actions for the tasks / chain-of-command module.
 *
 * Mirrors the pattern used by useContacts: module-scope refs (one shared store),
 * CRUD over the address-book service (`contactsApi` already targets it and carries
 * the auth interceptors), client-side filtering, a snackbar, and Promise-based
 * confirms for destructive actions.
 *
 * Tasks live in the address-book service under /api/tasks/*. Shared tags are
 * fetched through that service's tag proxy (/api/contacts/tags → assets service).
 */

import { ref, computed, watch } from "vue";
import { contactsApi, authApi } from "@/utils/api.js";
import { useConfirmDialog } from "@/composables/useConfirmDialog.js";
import { useUserPreferences } from "@/composables/useUserPreferences.js";

const { confirm } = useConfirmDialog();
const { usePreference } = useUserPreferences();

// Board layout, persisted per-user (server-backed, like contactsViewMode).
const defaultTasksViewMode = window.innerWidth < 600 ? "cards" : "list";
const viewMode = usePreference("tasksViewMode", defaultTasksViewMode);
function toggleViewMode() {
  viewMode.value = viewMode.value === "cards" ? "list" : "cards";
}

// ── Status lifecycle metadata ─────────────────────────────────────────────────
// `step` drives the vertical stepper position (0 = off-track for cancelled).
// Kept in sync with the backend's VALID_STATUSES in tasks.logic.js.
const STATUS_META = {
  waiting: { label: "Waiting", color: "grey", icon: "mdi-clock-outline", step: 1 },
  started: { label: "Started", color: "info", icon: "mdi-play", step: 2 },
  completed: { label: "Completed", color: "success", icon: "mdi-check-circle", step: 3 },
  cancelled: { label: "Cancelled", color: "error", icon: "mdi-close-circle", step: 0 },
};
const STATUS_ORDER = ["waiting", "started", "completed"]; // the linear path
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, m]) => ({ value, ...m }));

/** Metadata for a status value, falling back to a neutral shape for unknowns. */
function statusMeta(status) {
  return STATUS_META[status] || { label: status, color: "grey", icon: "mdi-help-circle", step: 0 };
}

/** Human-friendly date/time; returns "" for empty input. */
function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return "";
  return d.toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

/** Date only (used for the expiry field). */
function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

// ── Shared state ────────────────────────────────────────────────────────────
const tasks = ref([]);
const orgMembers = ref([]); // flat list; tree is derived
const reports = ref([]); // the current user's direct reports (assign targets)
const currentMember = ref(null); // the caller's org node
const canAssign = ref(false);
const isAppAdmin = ref(false);
const tags = ref([]); // shared tags for the tag autocomplete + chip rendering
const loading = ref(false);
const error = ref("");

// Filters (client-side, like contacts). statusFilter uses "all" as the
// no-filter sentinel so it can drive a mandatory chip-group cleanly.
const search = ref("");
const statusFilter = ref("all");
const filterTags = ref([]);
const assigneeFilter = ref(null); // filter the board by assignee (member id)
const selectedMemberId = ref(null); // org-node filter (set by clicking the tree)

// Sorting (chosen in the sort dialog, persisted with the other filters).
const sortField = ref("created"); // "due" | "created" | "tags"
const sortDir = ref("desc"); // "asc" | "desc"
const sortDialogOpen = ref(false);
const SORT_OPTIONS = [
  { value: "due", title: "Due date" },
  { value: "created", title: "Entered date" },
  { value: "tags", title: "Tags" },
];
function openSortDialog() {
  sortDialogOpen.value = true;
}

// Timeline dialog (shows the current filtered/sorted tasks on a timeline).
const timelineDialogOpen = ref(false);
function openTimelineDialog() {
  timelineDialogOpen.value = true;
}

// Persist the filter bar selections so they default to the last-used values.
// Initial defaults (before any selection) are "all" / empty.
const TASKS_FILTERS_KEY = "tasksFilters";
try {
  const saved = JSON.parse(localStorage.getItem(TASKS_FILTERS_KEY) || "null");
  if (saved) {
    if (typeof saved.search === "string") search.value = saved.search;
    if (typeof saved.statusFilter === "string") statusFilter.value = saved.statusFilter;
    if (saved.assigneeFilter !== undefined) assigneeFilter.value = saved.assigneeFilter;
    if (Array.isArray(saved.filterTags)) filterTags.value = saved.filterTags;
    if (typeof saved.sortField === "string") sortField.value = saved.sortField;
    if (typeof saved.sortDir === "string") sortDir.value = saved.sortDir;
  }
} catch {
  /* ignore malformed cache */
}
watch(
  [search, statusFilter, assigneeFilter, filterTags, sortField, sortDir],
  () => {
    localStorage.setItem(
      TASKS_FILTERS_KEY,
      JSON.stringify({
        search: search.value,
        statusFilter: statusFilter.value,
        assigneeFilter: assigneeFilter.value,
        filterTags: filterTags.value,
        sortField: sortField.value,
        sortDir: sortDir.value,
      }),
    );
  },
  { deep: true },
);

// Snackbar
const snackbar = ref({ show: false, message: "", color: "success" });
function showSnackbar(message, color = "success") {
  snackbar.value = { show: true, message, color };
}

// Assign/edit dialog state
const assignDialogOpen = ref(false);
const editingTask = ref(null); // null = create, object = edit
const saving = ref(false);
const emptyForm = () => ({
  title: "", description: "", notes: "", expiryDate: null,
  assignedToMemberId: null, tagIds: [], assetIds: [],
});
const form = ref(emptyForm());

// Detail dialog state
const detailDialogOpen = ref(false);
const detailTask = ref(null);
const statusSaving = ref(false);
const addingNote = ref(false);

// Org chart dialog + "add to chain" form state
const orgDialogOpen = ref(false);
const addMemberDialogOpen = ref(false);
const savingMember = ref(false);
const hrEmployees = ref([]); // HR employees available to graft onto the chart
const emptyMemberForm = () => ({ employeeId: null, name: "", email: "", title: "", managerId: null });
const memberForm = ref(emptyMemberForm());

// ── Derived data ──────────────────────────────────────────────────────────────

/** Nested org tree (root nodes with `children`), built from the flat list. */
const orgTree = computed(() => {
  const byId = new Map(orgMembers.value.map((m) => [m.id, { ...m, children: [] }]));
  const roots = [];
  for (const node of byId.values()) {
    if (node.manager_id && byId.has(node.manager_id)) {
      byId.get(node.manager_id).children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
});

/** Tag lookup for rendering chips by id. */
const tagsById = computed(() => new Map(tags.value.map((t) => [t.id, t])));

/** Filtered task list (search + status + tags), newest first (server order). */
const filteredTasks = computed(() => {
  let list = tasks.value;
  const q = search.value.trim().toLowerCase();
  if (q) {
    // Search across the task's text fields: title, description, notes.
    list = list.filter(
      (t) =>
        (t.title || "").toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q) ||
        (t.notes || "").toLowerCase().includes(q) ||
        (t.assigned_to_name || "").toLowerCase().includes(q) ||
        (t.assigned_by_name || "").toLowerCase().includes(q),
    );
  }
  if (statusFilter.value && statusFilter.value !== "all") {
    list = list.filter((t) => t.status === statusFilter.value);
  }
  if (assigneeFilter.value) {
    list = list.filter((t) => t.assigned_to_member_id === assigneeFilter.value);
  }
  if (filterTags.value.length) {
    list = list.filter((t) => filterTags.value.every((id) => (t.tag_ids || []).includes(id)));
  }
  if (selectedMemberId.value) {
    list = list.filter(
      (t) =>
        t.assigned_to_member_id === selectedMemberId.value ||
        t.assigned_by_member_id === selectedMemberId.value,
    );
  }
  return sortTasks(list);
});

/** First tag name of a task (for tag sorting); "" when untagged. */
function firstTagName(t) {
  const id = (t.tag_ids || [])[0];
  return id ? (tagsById.value.get(id)?.name || "").toLowerCase() : "";
}

/** Sort the filtered list by the chosen field/direction (a fresh array). */
function sortTasks(list) {
  const dir = sortDir.value === "asc" ? 1 : -1;
  const arr = [...list];
  if (sortField.value === "due") {
    // Tasks with a due date first (sorted), undated always at the end.
    const dated = arr.filter((t) => t.expiry_date);
    const undated = arr.filter((t) => !t.expiry_date);
    dated.sort((a, b) => (new Date(a.expiry_date) - new Date(b.expiry_date)) * dir);
    return [...dated, ...undated];
  }
  if (sortField.value === "tags") {
    const tagged = arr.filter((t) => firstTagName(t));
    const untagged = arr.filter((t) => !firstTagName(t));
    tagged.sort((a, b) => firstTagName(a).localeCompare(firstTagName(b)) * dir);
    return [...tagged, ...untagged];
  }
  // Default: entered date (created_at).
  arr.sort((a, b) => (new Date(a.created_at) - new Date(b.created_at)) * dir);
  return arr;
}

/** The org member currently used as a board filter (or null). */
const selectedMember = computed(
  () => orgMembers.value.find((m) => m.id === selectedMemberId.value) || null,
);

/** Distinct assignees present in the loaded tasks, for the assignee filter dropdown. */
const assigneeFilterOptions = computed(() => {
  const seen = new Map();
  for (const t of tasks.value) {
    if (t.assigned_to_member_id && !seen.has(t.assigned_to_member_id)) {
      seen.set(t.assigned_to_member_id, {
        value: t.assigned_to_member_id,
        title: t.assigned_to_name || t.assigned_to_email || `#${t.assigned_to_member_id}`,
      });
    }
  }
  return [...seen.values()].sort((a, b) => a.title.localeCompare(b.title));
});

/** Toggle the member filter from an org-tree node click. */
function selectMember(node) {
  selectedMemberId.value = selectedMemberId.value === node.id ? null : node.id;
}

// ── API actions ────────────────────────────────────────────────────────────────

async function fetchTasks() {
  loading.value = true;
  error.value = "";
  try {
    const { data } = await contactsApi.get("/api/tasks");
    tasks.value = data.tasks || [];
    currentMember.value = data.member || null;
    isAppAdmin.value = !!data.isAppAdmin;
    // Drop a persisted assignee filter that no longer matches any visible task.
    if (assigneeFilter.value && !tasks.value.some((t) => t.assigned_to_member_id === assigneeFilter.value)) {
      assigneeFilter.value = null;
    }
  } catch (err) {
    error.value = err.response?.data?.error || "Failed to load tasks";
  } finally {
    loading.value = false;
  }
}

async function fetchOrg() {
  try {
    const { data } = await contactsApi.get("/api/tasks/org");
    orgMembers.value = Array.isArray(data) ? data : [];
  } catch {
    orgMembers.value = [];
  }
}

async function fetchReports() {
  try {
    const { data } = await contactsApi.get("/api/tasks/org/reports");
    reports.value = data.reports || [];
    canAssign.value = !!data.canAssign;
    if (data.member) currentMember.value = data.member;
  } catch {
    reports.value = [];
    canAssign.value = false;
  }
}

async function fetchTags() {
  try {
    const { data } = await contactsApi.get("/api/contacts/tags");
    tags.value = Array.isArray(data) ? data : [];
  } catch {
    tags.value = [];
  }
}

/** Load everything the page needs in parallel. */
async function init() {
  await Promise.all([fetchTasks(), fetchOrg(), fetchReports(), fetchTags()]);
}

// ── Assign / edit ───────────────────────────────────────────────────────────────

/** Open the assign form in create mode, optionally pre-selecting an assignee. */
function openAssignDialog(prefillAssigneeId = null) {
  editingTask.value = null;
  form.value = { ...emptyForm(), assignedToMemberId: prefillAssigneeId };
  assignDialogOpen.value = true;
}

/** Open the assign form in edit mode, populated from an existing task. */
function openEditDialog(task) {
  editingTask.value = task;
  form.value = {
    title: task.title || "",
    description: task.description || "",
    notes: task.notes || "",
    expiryDate: task.expiry_date ? String(task.expiry_date).slice(0, 10) : null,
    assignedToMemberId: task.assigned_to_member_id || null,
    tagIds: [...(task.tag_ids || [])],
    assetIds: [...(task.asset_ids || [])],
  };
  // Close the detail dialog when switching to edit so the two dialogs don't both
  // render against the shared singleton state at once.
  detailDialogOpen.value = false;
  assignDialogOpen.value = true;
}

async function saveTask() {
  if (!form.value.title?.trim()) {
    showSnackbar("Title is required", "error");
    return;
  }
  if (!form.value.assignedToMemberId) {
    showSnackbar("Please choose who to assign the task to", "error");
    return;
  }
  saving.value = true;
  try {
    const payload = {
      title: form.value.title.trim(),
      description: form.value.description?.trim() || null,
      notes: form.value.notes?.trim() || null,
      expiryDate: form.value.expiryDate || null,
      assignedToMemberId: form.value.assignedToMemberId,
      tagIds: form.value.tagIds || [],
      assetIds: form.value.assetIds || [],
    };
    if (editingTask.value) {
      await contactsApi.put(`/api/tasks/${editingTask.value.id}`, payload);
      showSnackbar("Task updated");
    } else {
      await contactsApi.post("/api/tasks", payload);
      showSnackbar("Task assigned");
    }
    assignDialogOpen.value = false;
    await fetchTasks();
  } catch (err) {
    showSnackbar(err.response?.data?.error || "Failed to save task", "error");
  } finally {
    saving.value = false;
  }
}

// ── Detail / status / notes ──────────────────────────────────────────────────────

async function openDetail(taskId) {
  detailTask.value = null;
  detailDialogOpen.value = true;
  try {
    const { data } = await contactsApi.get(`/api/tasks/${taskId}`);
    detailTask.value = data;
  } catch (err) {
    showSnackbar(err.response?.data?.error || "Failed to load task", "error");
    detailDialogOpen.value = false;
  }
}

/** Change the open task's status, optionally with a note; refresh detail + list. */
async function changeStatus(status, note = null) {
  if (!detailTask.value) return;
  statusSaving.value = true;
  try {
    const { data } = await contactsApi.patch(`/api/tasks/${detailTask.value.id}/status`, {
      status,
      note,
    });
    detailTask.value = data;
    showSnackbar(`Marked as ${statusMeta(status).label}`);
    await fetchTasks();
  } catch (err) {
    showSnackbar(err.response?.data?.error || "Failed to update status", "error");
  } finally {
    statusSaving.value = false;
  }
}

async function addNote(content) {
  if (!detailTask.value || !content?.trim()) return;
  addingNote.value = true;
  try {
    await contactsApi.post(`/api/tasks/${detailTask.value.id}/notes`, { content: content.trim() });
    // Re-fetch detail so the new note appears in the timeline with author info.
    const { data } = await contactsApi.get(`/api/tasks/${detailTask.value.id}`);
    detailTask.value = data;
    await fetchTasks(); // keep note_count badges fresh
  } catch (err) {
    showSnackbar(err.response?.data?.error || "Failed to add note", "error");
  } finally {
    addingNote.value = false;
  }
}

/** Edit a note on the open task; refreshes the detail timeline. */
async function editTaskNote(noteId, content) {
  if (!detailTask.value || !content?.trim()) return;
  try {
    const { data } = await contactsApi.put(
      `/api/tasks/${detailTask.value.id}/notes/${noteId}`,
      { content: content.trim() },
    );
    detailTask.value = data;
  } catch (err) {
    showSnackbar(err.response?.data?.error || "Failed to edit note", "error");
  }
}

/** Delete a note from the open task (with confirmation); refreshes detail + list. */
async function deleteTaskNote(noteId) {
  if (!detailTask.value) return;
  const ok = await confirm({
    title: "Delete note?",
    message: "This note will be permanently removed.",
    type: "error",
    confirmText: "Delete",
  });
  if (!ok) return;
  try {
    const { data } = await contactsApi.delete(`/api/tasks/${detailTask.value.id}/notes/${noteId}`);
    detailTask.value = data;
    await fetchTasks();
  } catch (err) {
    showSnackbar(err.response?.data?.error || "Failed to delete note", "error");
  }
}

async function deleteTask(task) {
  const ok = await confirm({
    title: "Delete task?",
    message: `"${task.title}" and its notes/history will be permanently removed.`,
    type: "error",
    confirmText: "Delete",
  });
  if (!ok) return;
  try {
    await contactsApi.delete(`/api/tasks/${task.id}`);
    showSnackbar("Task deleted");
    if (detailTask.value?.id === task.id) detailDialogOpen.value = false;
    await fetchTasks();
  } catch (err) {
    showSnackbar(err.response?.data?.error || "Failed to delete task", "error");
  }
}

// ── Chain of command: add a member ──────────────────────────────────────────────

/** Load HR employees (for the "add to chain" picker). Cheap, cached after first call. */
async function fetchHrEmployees() {
  if (hrEmployees.value.length) return;
  try {
    const { data } = await contactsApi.get("/api/hr/employees", { params: { status: "active" } });
    hrEmployees.value = Array.isArray(data) ? data : [];
  } catch {
    hrEmployees.value = [];
  }
}

/** Open the add-member form, defaulting the manager to the current user's node. */
function openAddMember() {
  memberForm.value = { ...emptyMemberForm(), managerId: currentMember.value?.id ?? null };
  fetchHrEmployees();
  addMemberDialogOpen.value = true;
}

/**
 * Ensure the given email has a login with the "tasks" app so the new member can
 * actually access and update their tasks. Done from the frontend with the
 * appadmin's token (Add-to-chain is appadmin-only) via the existing admin API.
 * Returns "granted" | "already" | "missing" | "error".
 */
async function ensureTasksLogin(email) {
  const norm = (e) => String(e || "").toLowerCase().replace(/\+[^@]*@/, "@");
  const target = norm(email);
  try {
    const { data: users } = await authApi.get("/api/admin/users");
    const user = (users || []).find((u) => norm(u.email) === target);
    if (!user) return "missing";
    let apps = user.allowed_apps;
    if (typeof apps === "string") {
      try {
        apps = JSON.parse(apps);
      } catch {
        apps = [];
      }
    }
    if (!Array.isArray(apps)) apps = [];
    if (apps.includes("tasks")) return "already";
    await authApi.put(`/api/admin/users/${user.id}/allowed-apps`, { allowed_apps: [...apps, "tasks"] });
    return "granted";
  } catch {
    return "error";
  }
}

/** Create an org member from the form, then refresh the chart + grant Tasks login. */
async function addMember() {
  const f = memberForm.value;
  if (!f.name?.trim() || !f.email?.trim()) {
    showSnackbar("Name and email are required", "error");
    return;
  }
  if (!f.managerId) {
    showSnackbar("Choose who this person reports to", "error");
    return;
  }
  savingMember.value = true;
  try {
    const email = f.email.trim();
    await contactsApi.post("/api/tasks/org", {
      name: f.name.trim(),
      email,
      title: f.title?.trim() || null,
      managerId: f.managerId,
      employeeId: f.employeeId || null,
    });
    addMemberDialogOpen.value = false;
    await Promise.all([fetchOrg(), fetchReports()]);

    // (a) Make sure this person can actually access tasks: grant the "tasks" app
    // to their existing login, or warn that no login exists for the email.
    const login = await ensureTasksLogin(email);
    if (login === "missing") {
      showSnackbar(`Added. No login for ${email} — create one in User Management to give Tasks access.`, "warning");
    } else if (login === "granted") {
      showSnackbar("Added to the chain of command · granted Tasks access");
    } else {
      showSnackbar("Added to the chain of command");
    }
  } catch (err) {
    showSnackbar(err.response?.data?.error || "Failed to add member", "error");
  } finally {
    savingMember.value = false;
  }
}

// ── Tasks settings (digest cadence) ──────────────────────────────────────────
const settings = ref({ digestFrequency: "weekly", lastSentAt: null, options: ["off", "daily", "weekly", "biweekly"] });
const savingSettings = ref(false);
const sendingDigest = ref(false);

async function fetchSettings() {
  try {
    const { data } = await contactsApi.get("/api/tasks/settings");
    settings.value = data;
  } catch {
    /* appadmin-only; ignore for non-admins */
  }
}

async function saveSettings(frequency) {
  savingSettings.value = true;
  try {
    const { data } = await contactsApi.put("/api/tasks/settings", { digestFrequency: frequency });
    settings.value = { ...settings.value, ...data };
    showSnackbar("Settings saved");
  } catch (err) {
    showSnackbar(err.response?.data?.error || "Failed to save settings", "error");
  } finally {
    savingSettings.value = false;
  }
}

/** Trigger the digest + due reminders immediately (bypasses the cadence gate). */
async function sendDigestNow() {
  sendingDigest.value = true;
  try {
    const { data } = await contactsApi.post("/api/tasks/notify", { force: true });
    const n = (data?.digest?.sentTo || 0) + (data?.dueReminders?.sentTo || 0);
    showSnackbar(n ? `Sent ${n} email(s)` : "No emails to send (or mail not configured)");
    await fetchSettings();
  } catch (err) {
    showSnackbar(err.response?.data?.error || "Failed to send", "error");
  } finally {
    sendingDigest.value = false;
  }
}

export function useTasks() {
  return {
    // state
    tasks, orgMembers, orgTree, reports, currentMember, canAssign, isAppAdmin,
    tags, tagsById, loading, error,
    // filters
    search, statusFilter, filterTags, filteredTasks,
    assigneeFilter, assigneeFilterOptions,
    selectedMemberId, selectedMember, selectMember,
    // sorting
    sortField, sortDir, sortDialogOpen, openSortDialog, SORT_OPTIONS,
    // timeline dialog
    timelineDialogOpen, openTimelineDialog,
    // view layout
    viewMode, toggleViewMode,
    // snackbar
    snackbar, showSnackbar,
    // assign/edit
    assignDialogOpen, editingTask, form, saving,
    openAssignDialog, openEditDialog, saveTask,
    // detail/status/notes
    detailDialogOpen, detailTask, statusSaving, addingNote,
    openDetail, changeStatus, addNote, deleteTask, editTaskNote, deleteTaskNote,
    // org chart dialog + add-member form
    orgDialogOpen, addMemberDialogOpen, savingMember, memberForm, hrEmployees,
    openAddMember, addMember, fetchHrEmployees,
    // settings (digest cadence)
    settings, savingSettings, sendingDigest, fetchSettings, saveSettings, sendDigestNow,
    // actions
    init, fetchTasks, fetchOrg, fetchReports, fetchTags,
    // helpers / constants
    statusMeta, formatDateTime, formatDate,
    STATUS_META, STATUS_ORDER, STATUS_OPTIONS,
  };
}
